import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type DragEvent, type ReactNode, useRef, useState } from 'react';

import { visuallyHidden } from '../../lib/visually-hidden';
import { cssVar } from '../../theme/tokens';
import { Button } from './Button';
import { type FieldProps, FormField, splitFieldProps, useFormField } from './FormField';

export interface FileDropzoneProps extends FieldProps {
  /** The file input's `accept`, e.g. `.xlsx,.csv`. */
  accept: string;
  file: File | null;
  onChange: (file: File | null) => void;
  /** Text in the empty zone (default "Drag a file here or"). */
  prompt?: string;
  /** Button text in the empty zone (default "Choose file"). */
  chooseLabel?: string;
  /**
   * `inline` (default): a row with a Choose file button. `tile`: a tall zone that opens the file
   * picker when clicked (v3 upload tiles), with `icon` above the prompt and `detail` under it.
   */
  variant?: 'inline' | 'tile';
  icon?: ReactNode;
  detail?: ReactNode;
}

/** Height of a `tile` zone (150 px in the design). */
const TILE_HEIGHT = `calc(${cssVar('space-12')} * 3)`;

const tileLink = {
  color: 'inherit',
  typography: 'caption',
  fontWeight: 700,
  textDecorationColor: 'currentColor',
} as const;

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

function Zone({
  accept,
  file,
  onChange,
  prompt = 'Drag a file here or',
  chooseLabel = 'Choose file',
  variant = 'inline',
  icon,
  detail,
  name,
}: Omit<FileDropzoneProps, keyof FieldProps> & { name: string }) {
  const { controlId, describedBy, invalid, required, disabled, size } = useFormField();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const choose = () => input.current?.click();
  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled && event.dataTransfer.files[0]) onChange(event.dataTransfer.files[0]);
  };

  return (
    <>
      <input
        ref={input}
        id={controlId}
        type="file"
        accept={accept}
        aria-label={name}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        required={required}
        disabled={disabled}
        style={visuallyHidden}
        onChange={(event) => {
          onChange(event.target.files?.[0] ?? null);
          // Choosing the same file again still fires a change.
          event.target.value = '';
        }}
      />
      {variant === 'tile' ? (
        file ? (
          <Stack
            sx={{
              minHeight: TILE_HEIGHT,
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              px: 4,
              py: 4,
              textAlign: 'center',
              borderRadius: cssVar('radius-lg'),
              bgcolor: invalid ? cssVar('danger-subtle') : cssVar('success-subtle'),
              color: invalid ? cssVar('danger-text') : cssVar('success-text'),
              '& > .MuiSvgIcon-root': { fontSize: cssVar('space-7') },
            }}
          >
            {invalid ? (
              <ErrorOutlineRoundedIcon aria-hidden />
            ) : (
              <CheckCircleRoundedIcon aria-hidden />
            )}
            <Typography variant="bodySm" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
              <span>{file.name}</span> · <span>{formatFileSize(file.size)}</span>
            </Typography>
            <Stack direction="row" sx={{ gap: 3 }}>
              <Link
                component="button"
                type="button"
                onClick={choose}
                disabled={disabled}
                sx={tileLink}
              >
                Replace
              </Link>
              <Link
                component="button"
                type="button"
                onClick={() => onChange(null)}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
                sx={tileLink}
              >
                Remove
              </Link>
            </Stack>
          </Stack>
        ) : (
          <Stack
            component="label"
            htmlFor={controlId}
            onDragOver={(event) => {
              event.preventDefault();
              if (!disabled) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            sx={{
              minHeight: TILE_HEIGHT,
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              px: 4,
              py: 4,
              textAlign: 'center',
              cursor: disabled ? 'default' : 'pointer',
              border: `1px dashed ${
                dragging
                  ? cssVar('focus-ring')
                  : invalid
                    ? cssVar('danger-text')
                    : cssVar('border-strong')
              }`,
              borderRadius: cssVar('radius-lg'),
              bgcolor: dragging ? cssVar('hover') : cssVar('surface-sunken'),
              color: cssVar('text-muted'),
              '&:hover': disabled ? undefined : { bgcolor: cssVar('hover') },
              // The file input is visually hidden: the tile shows its keyboard focus.
              'input:focus-visible + &': {
                outline: `2px solid ${cssVar('focus-ring')}`,
                outlineOffset: '2px',
              },
              '& .MuiSvgIcon-root': { fontSize: cssVar('space-7') },
            }}
          >
            <Box aria-hidden sx={{ display: 'flex' }}>
              {icon ?? <CloudUploadOutlinedIcon />}
            </Box>
            <Typography variant="bodySm" component="span" sx={{ fontWeight: 600 }}>
              {prompt}
            </Typography>
            {detail ? (
              <Typography variant="caption" component="span" sx={{ color: cssVar('text-subtle') }}>
                {detail}
              </Typography>
            ) : null}
          </Stack>
        )
      ) : file ? (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 3,
            minHeight: cssVar('control-h-lg'),
            px: 3,
            py: 2,
            border: `1px solid ${invalid ? cssVar('danger-text') : cssVar('border')}`,
            borderRadius: cssVar('radius-md'),
            bgcolor: cssVar('surface'),
          }}
        >
          <InsertDriveFileOutlinedIcon aria-hidden sx={{ color: cssVar('text-muted') }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {file.name}
            </Typography>
            <Typography variant="caption" sx={{ color: cssVar('text-muted') }}>
              {formatFileSize(file.size)}
            </Typography>
          </Box>
          <Button variant="ghost" size={size} onClick={choose} disabled={disabled}>
            Replace
          </Button>
          <Button
            variant="ghost"
            size={size}
            onClick={() => onChange(null)}
            disabled={disabled}
            aria-label={`Remove ${file.name}`}
          >
            Remove
          </Button>
        </Stack>
      ) : (
        <Stack
          onDragOver={(event) => {
            event.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          direction={{ xs: 'column', sm: 'row' }}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 5,
            px: 4,
            border: 2,
            borderStyle: 'dashed',
            borderRadius: cssVar('radius-md'),
            borderColor: dragging
              ? cssVar('focus-ring')
              : invalid
                ? cssVar('danger-text')
                : cssVar('border-strong'),
            bgcolor: dragging ? 'action.hover' : cssVar('surface'),
            textAlign: 'center',
          }}
        >
          <CloudUploadOutlinedIcon aria-hidden sx={{ color: cssVar('text-muted') }} />
          <Typography variant="body2">{prompt}</Typography>
          <Button variant="outline" size={size} onClick={choose} disabled={disabled}>
            {chooseLabel}
          </Button>
        </Stack>
      )}
    </>
  );
}

/**
 * A file picker with a drop zone, inside the usual FormField (label above, hint and error
 * below). The chosen file shows its name and size with Replace and Remove; nothing is uploaded
 * here, the form decides what to do with the file.
 */
export function FileDropzone(props: FileDropzoneProps) {
  const { field, control } = splitFieldProps(props);
  const name = typeof field.label === 'string' ? field.label : 'File';
  return (
    <FormField {...field}>
      <Zone {...control} name={name} />
    </FormField>
  );
}
